import { Route } from "react-router-dom";
import { GameCommandByPlayer, LobbyGame, LobbySyncAdapterProps } from "./infra";
import {
  AllProviders,
  XGame,
  XRoutesContainer,
  gameContextFactory,
  lobbyContextFactory,
  useSessionUser,
} from "./jsx";
import { Command, FunctionCollection, Validation, deepCopy } from "./lib";
import { render } from "./render";
import { Base64 } from "./utils";

const defaultPathName =
  (typeof window !== "undefined" && window.location.pathname) || "";

export const getDefaultGameLobby = (
  pathname = defaultPathName
): LobbyGame | undefined => {
  const lastIndex = pathname.lastIndexOf("/");
  if (lastIndex === -1) return undefined;
  const gameData = pathname.substring(lastIndex + 1);
  try {
    const gameLobby = JSON.parse(Base64.decode(gameData));
    return gameLobby as LobbyGame;
  } catch (err) {
    console.warn(err);
    return undefined;
  }
};

export const discreteGameBuilder = <Game, Api extends FunctionCollection>({
  syncAdapterProps,
  gameApi,
  copyGame = deepCopy,
  isValidCommand,
  newGame,
  dummyGame,
  getLobbyGame = getDefaultGameLobby,
}: {
  syncAdapterProps: LobbySyncAdapterProps;
  gameApi: (game: Game, playerId: string) => Api;
  copyGame?: (game: Game) => Game;
  isValidCommand: (
    game: Game,
    command: GameCommandByPlayer<Command<ReturnType<typeof gameApi>>>
  ) => Validation;
  newGame: (lobbyGame: LobbyGame) => Game;
  dummyGame: Game;
  getLobbyGame?: () => LobbyGame | undefined;
}) => {
  type GameCommand = Command<ReturnType<typeof gameApi>>;

  const commandBuilder = Command.builder(gameApi(dummyGame, ""));
  const { LobbyProvider, useLobby } = lobbyContextFactory({
    lobbySyncAdapterProps: syncAdapterProps,
  });

  const {
    GameProvider,
    useGame,
    useGameOpt,
    useReplayContext,
    GameReplayProvider,
  } = gameContextFactory<Game, GameCommand>({
    gameReducerFactoryProps: {
      applyCommand: (game: Game, command: GameCommandByPlayer<GameCommand>) =>
        Command.call(gameApi(game, command.playerId), command),
      copyGame,
      isValidCommand,
    },
    newGame,
    syncAdapterProps,
    useLobbyGame: getLobbyGame,
  });

  const GameWithProviders = ({
    children,
  }: {
    children: React.ReactElement;
  }) => {
    return (
      <AllProviders>
        <GameProvider>
          <XGame useGame={useGame}>{children}</XGame>
        </GameProvider>
      </AllProviders>
    );
  };

  const AllLobbyProviders = ({
    children,
  }: {
    children: React.ReactElement;
  }) => {
    return (
      <AllProviders>
        <LobbyProvider>{children}</LobbyProvider>
      </AllProviders>
    );
  };

  const routerFactory = ({
    GameContent,
    LobbyContent,
  }: {
    GameContent: () => JSX.Element;
    LobbyContent: () => JSX.Element;
  }) => {
    const routesBuilder = {
      lobby: "/lobby",
      game: (gameId: string, gameData: LobbyGame) =>
        `/game/${gameId}/${Base64.encode(JSON.stringify(gameData))}`,
    };

    const standardRoutes = (
      <>
        <Route
          path={routesBuilder.lobby}
          element={
            <AllLobbyProviders>
              <LobbyContent />
            </AllLobbyProviders>
          }
        />
        <Route
          path="/game/:gameId/:gameData"
          element={
            <GameWithProviders>
              <GameContent />
            </GameWithProviders>
          }
        />
      </>
    );

    const StandardRouter = () => (
      <XRoutesContainer
        defaultRoute={routesBuilder.lobby}
        notFountPage={<>Not found</>}
      >
        {standardRoutes}
      </XRoutesContainer>
    );

    return { routesBuilder, standardRoutes, StandardRouter };
  };

  const useCommandAction = () => {
    const { game, update } = useGame();
    const sessionUser = useSessionUser();
    return (command: GameCommand) => {
      const validation = isValidCommand(game, {
        ...command,
        playerId: sessionUser.id,
      });
      return {
        validation,
        action: validation ? undefined : () => update(command),
      };
    };
  };

  return {
    render,
    GameProvider,
    commandBuilder,
    LobbyProvider,
    useLobby,
    useGame,
    useGameOpt,
    GameWithProviders,
    AllLobbyProviders,
    routerFactory,
    useLobbyGame: getLobbyGame,
    useSessionUser,
    useCommandAction,
    AllProviders,
    useReplayContext,
    GameReplayProvider,
  };
};
